import { Controller, useForm } from 'react-hook-form';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BillingKeys, updateAchInfo } from '../../../api/billing-api';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import {
  BillingInformationModel,
  StripeAchPaymentSourceModel,
} from '../../../api/billing-types';

import dayjs from 'dayjs';
import AvatarChip from '../../../shared/avatar-chip';
import VerifyBankAccountDialog from './verify-bank-account-dialog';

import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AssuredWorkload from '@mui/icons-material/AssuredWorkload';
import Save from '@mui/icons-material/Save';

interface PaymentMethodEditAchFormProps {
  billingRoles: string[];
  changeLogRoles: string[];
  paymentMethod: StripeAchPaymentSourceModel;
  billingInfoModel: BillingInformationModel;
}

export default function PaymentMethodEditAchForm({
  billingRoles,
  changeLogRoles,
  paymentMethod,
  billingInfoModel,
}: PaymentMethodEditAchFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openVerifyBankAccountDialog, setOpenVerifyBankAccountDialog] =
    useState(false);

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<StripeAchPaymentSourceModel>({
    defaultValues: {
      ...paymentMethod,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: StripeAchPaymentSourceModel) =>
      updateAchInfo(billingInfoModel, data, changeLogMessage),
    onSuccess: (returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Payment method updated successfully!',
      });

      reset(data);

      queryClient.setQueryData([BillingKeys.CustomerBillingInformation], {
        ...billingInfoModel,
        stripeSourceModels: billingInfoModel.stripeSourceModels.map((source) =>
          source.id === returnedData.id ? data : source
        ),
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the payment method. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.edit_payment_method, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
      });
    },
  });

  const onSubmit = (data: StripeAchPaymentSourceModel) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canEditPaymentMethods.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const canEditPaymentMethods = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canEditPaymentMethods.value
  );
  const hasCreatedDate = paymentMethod.metadata?.createdDate !== '0';
  const hasCreatedBy = !!paymentMethod.metadata?.createdBy;

  return (
    <Box>
      <AccordionDetails>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%', my: 0.5 }}
        >
          <Stack direction="row" spacing={2}>
            <Typography variant="caption">
              Account number: x{paymentMethod.last4}
            </Typography>
            <Typography variant="caption">
              Routing number: {paymentMethod.routing_number}
            </Typography>
          </Stack>
          <Typography variant="caption">
            {hasCreatedDate
              ? `created on ${dayjs(paymentMethod.metadata?.createdDate).format(
                  'MM/DD/YYYY'
                )}`
              : ''}
            {hasCreatedDate && hasCreatedBy && ' by '}
            {hasCreatedBy && (
              <AvatarChip
                email={paymentMethod.metadata?.createdBy || ''}
                type="User"
              />
            )}
          </Typography>
        </Stack>
        <Divider sx={{ mt: 1, mb: 2 }} />

        <Tooltip
          arrow
          title={
            canEditPaymentMethods
              ? ''
              : `You need the permission "${BillingRoles.canEditPaymentMethods.name}"  to edit this payment method.`
          }
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Controller
                name="account_holder_name"
                rules={{
                  required: `Please provide an account holder name.`,
                }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name on Account"
                    error={!!errors.account_holder_name}
                    helperText={errors.account_holder_name?.message || ' '}
                    size="small"
                    fullWidth
                    required
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Controller
                name="account_holder_type"
                rules={{
                  required: `Please provide an account holder type.`,
                }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account Holder Type"
                    error={!!errors.account_holder_type}
                    helperText={errors.account_holder_type?.message || ' '}
                    size="small"
                    fullWidth
                    required
                    select
                    disabled={!canEditPaymentMethods}
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="company">Company</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Controller
                name="metadata.nickName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nickname"
                    error={!!errors.metadata?.nickName}
                    helperText={errors.metadata?.nickName?.message || ' '}
                    size="small"
                    fullWidth
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Tooltip>

        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => mutate(getValues())}
          mutateLoading={isPending}
        />
        <VerifyBankAccountDialog
          open={openVerifyBankAccountDialog}
          handleClose={() => setOpenVerifyBankAccountDialog(false)}
          billingInfoModel={billingInfoModel}
          selectedMethod={paymentMethod}
        />
      </AccordionDetails>
      <AccordionActions>
        {paymentMethod.showWarning && (
          <Button
            variant="outlined"
            color="warning"
            endIcon={<AssuredWorkload />}
            onClick={() => {
              setOpenVerifyBankAccountDialog(true);
            }}
          >
            Verify
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          loading={isPending}
          disabled={!isDirty}
          onClick={handleSubmit(onSubmit)}
          endIcon={<Save />}
        >
          Save
        </Button>
      </AccordionActions>
    </Box>
  );
}
